import React, { useEffect } from 'react';
import useLocale from '@/hooks/useLocale';
import {
  Button, IconCheck, IconShield, Link, Modal,
} from 'gatorcomponents';
import * as Styles from './MigrateToGatorProtectModal.styles';
import gatorProtectBlueShield from '@/media/gatorProtect/gatorProtectBlueShield.png';
import snappySmall from '@/media/gatorProtect/snappy-small.png';
import type {
  GatorProtectLocale, MigrateToGatorProtectModalLocale, ConsultedProduct, MountCardProps, GradientButtonProps,
} from './MigrateToGatorProtectModal.types';
import { GATOR_PROTECT_ID } from '@/pages/GatorProtect/GatorProtectHandler/GatorProtect.enum';
import GradientButton from '@/components/Buttons/GradientButton/GradientButton';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { SERVICE_TERMS } from '@/config/urls/supportUrls';

export const MountItems = (list: string[]) => list.map(item => (
  <Styles.ListItem key={item}>
    <IconCheck color="primaryBlue" size="sm"/>
    {item}
  </Styles.ListItem>
));

export const MountCard = ({
  tag,
  title,
  list,
  cta,
  lightCard = false,
  gradient = false,
  terms,
  termsCta,
  onClick,
}:MountCardProps) => (
  <Styles.Card $lightCard={lightCard}>
    <Styles.CardTitle $lightCard={lightCard}>
      <Styles.CardTag>{tag}</Styles.CardTag>
      {title}
    </Styles.CardTitle>

    <Styles.InnerCard>
      {MountItems(list)}

      {(terms && termsCta) && (
        <Styles.TermsText>
          {terms}
          <Link
            text={termsCta}
            href={SERVICE_TERMS}
            target="_blank"
          />
        </Styles.TermsText>
      )}

      <Styles.InnerButtonWrapper $gradient={gradient}>
        {gradient ? (
          React.createElement(GradientButton as React.FC<GradientButtonProps>, {
            testId: 'gator-protect-migrate-modal-pro-cta',
            iconLeft: <IconShield size="sm" />,
            label: cta,
            width: 'full',
            size: 'large',
            notFillSVG: true,
            onClick,
          })
        ) : (
          <Button
            variant="tertiary"
            label={cta}
            width="full"
            size="large"
            onClick={onClick}
          />
        )}


      </Styles.InnerButtonWrapper>
    </Styles.InnerCard>
  </Styles.Card>
);

const MigrateToGatorProtectModal = ({
  onClose,
  consultedProducts,
  getProPrice,
  handleRedirectToGatorProtect,
}:{
  onClose: () => void;
  consultedProducts?: ConsultedProduct[];
  getProPrice: () => void;
  handleRedirectToGatorProtect: () => void;
}) => {
  const gatorProtectProData = consultedProducts?.find(item => item.id === GATOR_PROTECT_ID.PRO);
  const { gatorProtect: gatorProtectLocale } = useLocale() as { gatorProtect: GatorProtectLocale };
  const { migrateToGatorProtectModal: migrateToGatorProtectModalLocale } = gatorProtectLocale as { migrateToGatorProtectModal: MigrateToGatorProtectModalLocale };

  const trialParams = {
    tag: 'LIGHT',
    title: migrateToGatorProtectModalLocale.light.title,
    list: migrateToGatorProtectModalLocale.light.list,
    cta: migrateToGatorProtectModalLocale.light.cta,
    lightCard: true,
    onClick: onClose,
  };

  const proParams = {
    tag: 'PRO',
    title: migrateToGatorProtectModalLocale.pro.title,
    list: migrateToGatorProtectModalLocale.pro.list,
    cta: migrateToGatorProtectModalLocale.pro.cta(gatorProtectProData?.prices?.pricing?.monthly
      ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly)
      : '...'),
    gradient: true,
    terms: migrateToGatorProtectModalLocale.pro.terms,
    termsCta: migrateToGatorProtectModalLocale.pro.termsCta,
    onClick: handleRedirectToGatorProtect,
  };

  useEffect(() => {
    getProPrice();
  }, [getProPrice]);

  return (
    <Modal
      onClose={onClose}
      maxWidth={800}
      maxHeight={10000}
      noHeader
      testId="gator-protect-migrate-modal"
      noContentPadding
    >
      <Styles.ModalContent>
        <Styles.LogoWrapper>
          <img src={gatorProtectBlueShield} alt="Gator Protect Blue Shield" />
        </Styles.LogoWrapper>

        <Styles.Title>
          {migrateToGatorProtectModalLocale.title}
        </Styles.Title>

        <Styles.Description>
          {migrateToGatorProtectModalLocale.description}
        </Styles.Description>

        <Styles.CardsWrapper>
          {MountCard({ ...trialParams })}

          {MountCard({ ...proParams })}
        </Styles.CardsWrapper>

        <Styles.SnappyImage src={snappySmall}/>
      </Styles.ModalContent>
    </Modal>
  );
};


export default MigrateToGatorProtectModal;
